/* «Chofer XXX > tractor YYY», sin confundir lo dicho con lo medido.
 *
 * ── LAS DOS COSAS QUE NO SON LA MISMA ─────────────────────────────────────
 *
 * ASIGNADO   lo escribió una persona en `chofer_tractor`. Es una decisión:
 *            «este camión es de Fulano». No caduca sola y no se mide.
 *
 * HABITUAL   lo cuenta `tractor_habitual` sobre las salidas de portería:
 *            «44 de 45 salidas, las últimas 12 seguidas». Es una tendencia,
 *            con su número al lado, y cambia cada vez que alguien cruza el
 *            portón.
 *
 * Se contradicen seguido y por motivos normales: un chofer que estuvo tres
 * semanas de licencia tiene la tendencia del que lo reemplazó y sigue
 * teniendo su camión asignado. Por eso acá **gana siempre el asignado** —una
 * medición no le discute una decisión a la persona que la tomó— pero el
 * habitual NO se esconde cuando dice otra cosa. Que aparezcan los dos es lo
 * que deja darse cuenta de que la asignación quedó vieja; tapando el que
 * pierde, esa asignación se queda mal para siempre y nadie se entera.
 *
 * ── SIN ASIGNAR NO ES UN HUECO: ES UNA PROPUESTA ──────────────────────────
 *
 * La tabla nace vacía a propósito (sembrarla con el habitual sería convertir
 * una inferencia en una declaración sin que nadie la mire). Pero un casillero
 * en blanco al lado de un habitual del 98% es trabajo que ya está hecho y no
 * se aprovecha. Cuando no hay asignación y sí hay habitual confiable, esto
 * devuelve el habitual marcado como PROPUESTA — la pantalla lo dibuja distinto
 * y con un botón para adoptarlo. Un click de una persona, y recién ahí es una
 * declaración.
 *
 * Es puro: entran las filas, sale qué mostrar. Se prueba en una tabla.
 */

/* Las dos normalizaciones, y son las mismas que ya usa el resto del hub.
 *
 * El DNI viaja con puntos en algunas tablas y sin puntos en otras
 * (`claveDni` de `nominaSemaforo.js` hace exactamente esto). El interno viaja
 * como texto en `chofer_tractor` y como número en media docena de lugares, así
 * que `"581"`, `581` y `" 581 "` tienen que ser el mismo camión — si no, la
 * asignación se guarda con una forma y se busca con la otra, que es como se
 * pierde una fila sin que falle nada.
 */
export const claveDni = (v) =>
  String(v ?? "").replace(/\D+/g, "").replace(/^0+/, "");

export const claveInterno = (v) =>
  String(v ?? "").replace(/\D+/g, "").replace(/^0+/, "");

/* Cómo se dice un interno cuando se lo muestra: el número tal cual, sin ceros
 * de adelante. `""` cuando no hay, para que la pantalla no dibuje «el ». */
export const comoSeLlama = (v) => claveInterno(v) || "";

/* ── El lado de la PERSONA: ¿qué tractor tiene este chofer? ────────────────
 *
 * Devuelve SIEMPRE un objeto, nunca `null`, y con `estado` adentro. Los cuatro
 * estados mandan a hacer cosas distintas y dibujados igual se confunden:
 *
 *   asignado    lo tiene, lo dijo alguien. Se muestra y se puede editar.
 *   discrepa    lo tiene asignado, PERO suele salir con otro. Los dos a la
 *               vista: puede ser un reemplazo temporal o una asignación
 *               que quedó vieja, y eso lo sabe la persona, no la pantalla.
 *   propuesta   nadie lo asignó, pero portería dice que anda siempre con el
 *               mismo. Un botón, y es asignación.
 *   nada        ni asignación ni tendencia confiable. No hay nada que decir.
 */
export function tractorDelChofer(asignado, habitual) {
  const asig = claveInterno(asignado?.interno);
  const hab = claveInterno(habitual?.tractor);

  if (!asig) {
    if (!hab) return { estado: "nada" };
    return { estado: "propuesta", interno: hab, habitual };
  }

  const base = {
    interno: asig,
    desde: asignado?.desde || null,
    nota: asignado?.nota || null,
    asignado,
  };
  /* Sin habitual no hay contradicción posible, y con el MISMO tampoco: ahí la
   * tendencia confirma y no aporta un renglón aparte. */
  if (!hab || hab === asig) return { ...base, estado: "asignado" };
  return { ...base, estado: "discrepa", habitual };
}

/* ── El lado del TRACTOR: ¿quién tiene asignado el 581? ────────────────────
 *
 * DEVUELVE UNA LISTA Y NO UNA PERSONA. Está medido sobre las salidas de
 * portería de los últimos 180 días: 178 de los 307 tractores que salieron
 * llevaron más de un chofer (86 llevaron dos, 46 tres, y hay uno con nueve).
 * Doble turno, relevos y francos son lo normal, no la excepción. Un
 * `find()` acá mostraría uno de los dos —el que la base devolviera primero— y
 * el otro desaparecería de la pantalla sin dejar rastro.
 *
 * Ordena por `desde` descendente: el último que se lo asignaron primero, que
 * es el que se está buscando cuando se pregunta de quién es el camión.
 */
export function choferesDelTractor(filas, interno) {
  const k = claveInterno(interno);
  if (!k) return [];
  return (filas || [])
    .filter((f) => claveInterno(f?.interno) === k)
    .slice()
    .sort((a, b) => String(b?.desde || "").localeCompare(String(a?.desde || "")));
}

/* Las asignaciones indexadas por DNI, para no recorrer la lista por persona. */
export function porDni(filas) {
  const idx = {};
  for (const f of filas || []) {
    const k = claveDni(f?.dni);
    if (k) idx[k] = f;
  }
  return idx;
}

/* ── Cómo se dice, en una línea ───────────────────────────────────────────
 *
 * El mismo texto que usa FlotaBot (`asignacion.py`), porque el que pregunta
 * por WhatsApp y el que abre la ficha tienen que leer la misma frase o van a
 * creer que son dos datos distintos. Es la misma razón por la que
 * `ubicacion_chofer._COMO_SE_DICE` copia los textos de `shared/chofer.js`.
 */
export function comoSeDice(r) {
  if (!r || r.estado === "nada") return "";
  if (r.estado === "propuesta")
    return `Nadie se lo asignó. Suele salir con el ${r.interno}.`;
  const base = `Tiene asignado el ${r.interno}`;
  if (r.estado === "discrepa")
    return `${base}, pero últimamente sale con el ${claveInterno(r.habitual?.tractor)}.`;
  return `${base}.`;
}

/* La frase del lado del camión. Con dos choferes los nombra a los dos: elegir
 * uno sería inventar cuál de los dos turnos es «el» chofer. */
export function comoSeDiceElTractor(filas, nombreDe = (f) => f?.dni) {
  const n = (filas || []).length;
  if (!n) return "Nadie lo tiene asignado.";
  const quienes = filas.map((f) => nombreDe(f) || f?.dni).filter(Boolean);
  if (n === 1) return `${quienes[0]} lo tiene asignado.`;
  return `${quienes.slice(0, -1).join(", ")} y ${quienes[quienes.length - 1]} lo tienen asignado.`;
}
